import React, { useState, useEffect, useCallback } from 'react';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { Mark, mergeAttributes } from '@tiptap/core';

// Custom marks for our formatting types
const LogicalConnection = Mark.create({
  name: 'logicalConnection',
  addAttributes() {
    return {
      class: {
        default: 'logical-connection',
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'span.logical-connection',
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'logical-connection' }), 0];
  },
});

const RelevantInfo = Mark.create({
  name: 'relevantInfo',
  addAttributes() {
    return {
      class: {
        default: 'relevant-info',
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'span.relevant-info',
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'relevant-info' }), 0];
  },
});

const CriticalPhrase = Mark.create({
  name: 'criticalPhrase',
  addAttributes() {
    return {
      class: {
        default: 'critical-phrase',
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'span.critical-phrase',
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'critical-phrase' }), 0];
  },
});

const Fluff = Mark.create({
  name: 'fluff',
  addAttributes() {
    return {
      class: {
        default: 'fluff',
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'span.fluff',
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'fluff' }), 0];
  },
});

const Premise = Mark.create({
  name: 'premise',
  addAttributes() {
    return {
      class: {
        default: 'premise',
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'span.premise',
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'premise' }), 0];
  },
});

// Component for our WYSIWYG Editor
const TextEditor = ({ content, onChange }: { content?: string; onChange?: (...args: any) => void; }) => {
  // State to store the formatted output
  const [outputText, setOutputText] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Function to convert bracket format to HTML
  const convertFromBracketFormat = useCallback((text) => {
    if (!text) return '';
    
    // Regular expression to match our bracket format
    const regex = /\[\[(.*?)::([a-z-]+)\]\]/g;
    
    // Replace each match with the appropriate HTML
    const html = text.replace(regex, (match, content, formatType) => {
      switch (formatType) {
        case 'logical-connection':
          return `<span class="logical-connection">${content}</span>`;
        case 'relevant-info':
          return `<span class="relevant-info">${content}</span>`;
        case 'critical-phrase':
          return `<span class="critical-phrase">${content}</span>`;
        case 'fluff':
          return `<span class="fluff">${content}</span>`;
        case 'premise':
          return `<span class="premise">${content}</span>`;
        default:
          return content;
      }
    });
    
    // Wrap in paragraph tags if needed
    const paragraphs = html.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
    return paragraphs.map(p => `<p>${p}</p>`).join('');
  }, []);

  // Initialize the editor
  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      StarterKit.configure({
        document: false,
        paragraph: false,
        text: false,
      }),
      Placeholder.configure({
        placeholder: 'Start typing or paste text here...',
      }),
      LogicalConnection,
      RelevantInfo,
      CriticalPhrase,
      Fluff,
      Premise,
    ],
    // Convert content from bracket format to HTML before initializing
    content: content ? convertFromBracketFormat(content) : '',
    onUpdate: ({ editor }) => {
      onChange && onChange(convertToOutputFormat(editor.getHTML()));

      // Update the output text when the editor content changes
      setOutputText(convertToOutputFormat(editor.getHTML()));
    },
  });

  // Function to convert HTML to our bracket format
  const convertToOutputFormat = useCallback((html) => {
    // Create a temporary element to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Function to process nodes recursively
    const processNodes = (parentNode) => {
      let result = '';
      
      for (const node of parentNode.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          // Handle text nodes
          result += node.textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Handle element nodes
          if (node.tagName.toLowerCase() === 'p') {
            // Process paragraphs
            result += processNodes(node);
            if (!result.endsWith('\n')) {
              result += '\n\n';
            }
          } else if (node.tagName.toLowerCase() === 'span') {
            // Handle our custom formatting spans
            const content = processNodes(node);
            if (node.classList.contains('logical-connection')) {
              result += `[[${content}::logical-connection]]`;
            } else if (node.classList.contains('relevant-info')) {
              result += `[[${content}::relevant-info]]`;
            } else if (node.classList.contains('critical-phrase')) {
              result += `[[${content}::critical-phrase]]`;
            } else if (node.classList.contains('fluff')) {
              result += `[[${content}::fluff]]`;
            } else if (node.classList.contains('premise')) {
              result += `[[${content}::premise]]`;
            } else {
              result += content;
            }
          } else {
            // Process other elements
            result += processNodes(node);
          }
        }
      }
      
      return result;
    };
    
    return processNodes(tempDiv);
  }, []);

  // Add a function to handle pasting bracket format text
  const handlePaste = useCallback((e) => {
    if (!editor) return;
    
    // Get the pasted text
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedText = clipboardData.getData('text/plain');
    
    // Check if it contains our bracket format
    if (pastedText.includes('[[') && pastedText.includes('::')) {
      e.preventDefault();
      
      // Convert to HTML and insert
      const html = convertFromBracketFormat(pastedText);
      editor.commands.insertContent(html);
    }
  }, [editor, convertFromBracketFormat]);

  // Add effect to handle the paste event
  useEffect(() => {
    if (!editor) return;
    
    const editorElement = editor.view.dom;
    editorElement.addEventListener('paste', handlePaste);
    
    return () => {
      editorElement.removeEventListener('paste', handlePaste);
    };
  }, [editor, handlePaste]);

  // Function to apply formatting
  const applyFormatting = (formatType) => {
    if (!editor) return;
    
    switch (formatType) {
      case 'logical-connection':
        editor.chain().focus().toggleMark('logicalConnection').run();
        break;
      case 'relevant-info':
        editor.chain().focus().toggleMark('relevantInfo').run();
        break;
      case 'critical-phrase':
        editor.chain().focus().toggleMark('criticalPhrase').run();
        break;
      case 'fluff':
        editor.chain().focus().toggleMark('fluff').run();
        break;
      case 'premise':
        editor.chain().focus().toggleMark('premise').run();
        break;
      case 'clear':
        editor.chain().focus()
          .unsetMark('logicalConnection')
          .unsetMark('relevantInfo')
          .unsetMark('criticalPhrase')
          .unsetMark('fluff')
          .unsetMark('premise')
          .run();
        break;
      default:
        break;
    }
  };

  // Function to toggle preview mode
  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="bg-white shadow-md rounded p-4 mb-4">
        {(
          <>
            {/* Toolbar */}
            {/* <div className="flex flex-wrap gap-2 mb-4 p-2 bg-gray-100 rounded-lg">
              <button
                onClick={() => applyFormatting('logical-connection')}
                className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                title="Mark logical connections that reveal reasoning pathways"
              >
                Logical Connection
              </button>
              <button
                onClick={() => applyFormatting('relevant-info')}
                className="px-2 py-1 bg-yellow-400 text-black rounded hover:bg-yellow-500 text-sm"
                title="Highlight relevant information in the passage"
              >
                Relevant Info
              </button>
              <button
                onClick={() => applyFormatting('critical-phrase')}
                className="px-2 py-1 bg-orange-400 text-black rounded hover:bg-orange-500 text-sm"
                title="Mark critical phrases that should not be overlooked"
              >
                Critical Phrase
              </button>
              <button
                onClick={() => applyFormatting('fluff')}
                className="px-2 py-1 bg-gray-400 text-white rounded hover:bg-gray-500 text-sm"
                title="Mark misleading or unnecessary fluff"
              >
                Fluff
              </button>
              <button
                onClick={() => applyFormatting('premise')}
                className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                title="Highlight premises in an argument"
              >
                Premise
              </button>
              <button
                onClick={() => applyFormatting('clear')}
                className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm ml-auto"
                title="Clear formatting from selected text"
              >
                Clear Format
              </button>
            </div> */}
            
            {/* Editor */}
            <div className="border rounded-lg p-4 min-h-64 mb-4">
              <EditorContent editor={editor} className="prose max-w-none" />
            </div>
            
            {editor && (
              <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }}>
                <div className="flex gap-1 bg-white shadow-lg rounded-lg p-1 border">
                  <button
                    onClick={() => applyFormatting('logical-connection')}
                    className="p-1 text-xs bg-blue-100 hover:bg-blue-200 rounded"
                  >
                    Logical
                  </button>
                  <button
                    onClick={() => applyFormatting('relevant-info')}
                    className="p-1 text-xs bg-yellow-100 hover:bg-yellow-200 rounded"
                  >
                    Relevant
                  </button>
                  <button
                    onClick={() => applyFormatting('critical-phrase')}
                    className="p-1 text-xs bg-orange-100 hover:bg-orange-200 rounded"
                  >
                    Critical
                  </button>
                  <button
                    onClick={() => applyFormatting('fluff')}
                    className="p-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    Fluff
                  </button>
                  <button
                    onClick={() => applyFormatting('premise')}
                    className="p-1 text-xs bg-green-100 hover:bg-green-200 rounded"
                  >
                    Premise
                  </button>
                  <button
                    onClick={() => applyFormatting('clear')}
                    className="p-1 text-xs bg-red-100 hover:bg-red-200 rounded"
                  >
                    Clear
                  </button>
                </div>
              </BubbleMenu>
            )}
          </>
        )}
        
        {/* Preview or Bracket Format Output */}
        {/* {showPreview ? (
          <div className="border rounded-lg p-4 mb-4">
            <h3 className="font-bold mb-2">Formatted Preview</h3>
            <div className="prose max-w-full">
              <div>
                <FormattedTextViewer content={{text: outputText, format: "brackets" }} />
              </div>
            </div>
          </div>
        ) : (
          <div className="border rounded-lg p-4 mb-4">
            <h3 className="font-bold mb-2">Bracket Format Output</h3>
            <pre className="bg-gray-100 p-2 rounded overflow-auto max-h-64 whitespace-pre-wrap">
              {outputText}
            </pre>
          </div>
        )} */}
        
        <div className="flex justify-end">
          {/* <button
            onClick={togglePreview}
            className="px-3 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
          >
            {showPreview ? "Edit Text" : "Show Preview"}
          </button> */}
          
          <button
            onClick={() => {
              navigator.clipboard.writeText(outputText);
              alert("Output copied to clipboard!");
            }}
            className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Copy to Clipboard
          </button>
        </div>
      </div>
      
      {/* Styling for the editor */}
      <style jsx global>{`
        .ProseMirror {
          outline: none;
          min-height: 200px;
        }
        
        .ProseMirror p {
          margin: 0.5em 0;
        }
        
        /* Styles that match our viewer component */
        .ProseMirror .logical-connection {
          font-weight: bold;
          color: #2563eb;
          text-decoration: underline;
        }
        
        .ProseMirror .relevant-info {
          background-color: #fef08a;
          padding: 0 0.25em;
          border-radius: 0.25em;
        }
        
        .ProseMirror .critical-phrase {
          background-color: #fdba74;
          font-weight: bold;
          padding: 0 0.25em;
          border-radius: 0.25em;
          border-bottom: 2px solid #f97316;
        }
        
        .ProseMirror .fluff {
          text-decoration: line-through;
          color: #9ca3af;
        }
        
        .ProseMirror .premise {
          background-color: #bbf7d0;
          padding: 0 0.25em;
          border-radius: 0.25em;
          border-left: 4px solid #22c55e;
          margin-left: 0.25em;
        }
      `}</style>
    </div>
  );
};

export default TextEditor;
